// ---- Campfires (client, plan 25 + firestart activity) ---------------------------
// The player-started hearth mirror: live fires everyone on the map sees
// (`fires`/`fire`). Starting a fire is bag-only (kindling → Start fire →
// adjacent orange tile pick → spark minigame in firestarting.ts).
//
// Click-to-focus: fire groups are raycastable via registerFirePicker (focus.ts).
// Focused fires show Cook (any) + Extinguish (mine only) + a burn-timer banner.
// The server owns burn-out; cooking unions this mirror via setLiveFires.

import * as THREE from "three";
import { heightAt } from "./terrain.ts";
import { DECOS } from "./deco/index.ts";
import { buildDeco } from "./deco/define.ts";
import { makeFlames, updateFlames } from "./cookfx.ts";
import { setLiveFires } from "./cooking.ts";
import {
  registerFirePicker, getFireFocus, refreshFireFocus, clearFireFocus,
  type FireHandle,
} from "./focus.ts";
import { COOKING_TUNING as CT } from "./tunables.ts";
import { game, inputLocked } from "./state.ts";
import { sendExtinguishFire } from "./net.ts";
import { flashToast } from "./inventory.ts";
import { playSfx, preloadSfx, setAmbientEmitters } from "./audio.ts";
import type { FireDeploy } from "./shared.ts";

/** Ignite one-shot when a fire group is newly spawned (not on join full-sync). */
const IGNITE_SFX = "campfire-ignite";
/** Soft proximity loop around every lit fire. */
const AMBIENT_TRACK = "campfire-ambient";
/** Full crackle inside this radius; silent past outer. */
const AMBIENT_INNER = 4;
const AMBIENT_OUTER = 14;
/** Soft bed under map ambience (0..1 trim on the ambience bus). */
const AMBIENT_VOLUME = 0.38;

let scene: THREE.Scene;
let onFiresChanged: () => void = () => {};

// ---- Burning-fire mirror (server-owned; everyone sees the blaze) ----------------
const firesMap = new Map<string, FireDeploy>();
const fireGroups = new Map<string, THREE.Group>();

function buildFireGroup(seed: number, id: string): THREE.Group {
  const g = new THREE.Group();
  g.userData.fireId = id;
  const spec = DECOS.find((d) => d.type === "campfire");
  if (spec) g.add(buildDeco(spec, seed));
  const flames = makeFlames(1);
  g.add(flames);
  g.userData.flames = flames;
  return g;
}

function fireHandle(f: FireDeploy): FireHandle {
  return {
    id: f.id,
    x: f.x,
    y: heightAt(f.x, f.z),
    z: f.z,
    radius: 0.9,
    mine: !!f.mine,
    readyAt: f.readyAt,
  };
}

function addFireGroup(f: FireDeploy): void {
  const g = buildFireGroup(firesMap.size + 1, f.id);
  g.position.set(f.x, heightAt(f.x, f.z), f.z);
  scene.add(g);
  fireGroups.set(f.id, g);
}

function removeFireGroup(id: string): void {
  const g = fireGroups.get(id);
  if (g) scene.remove(g);
  fireGroups.delete(id);
  if (getFireFocus()?.id === id) clearFireFocus();
}

/** Push every live fire into the audio mixer's proximity-emitter list. */
function syncFireAmbience(): void {
  setAmbientEmitters(
    [...firesMap.values()].map((f) => ({
      track: AMBIENT_TRACK,
      x: f.x,
      z: f.z,
      r: AMBIENT_OUTER,
      inner: AMBIENT_INNER,
      volume: AMBIENT_VOLUME,
    })),
  );
}

/** Full mirror replace (join/reconnect/transfer). No ignite SFX — those fires
 *  were already burning; only the soft ambient bed re-registers. */
export function setFires(list: FireDeploy[]): void {
  for (const id of [...fireGroups.keys()]) removeFireGroup(id);
  firesMap.clear();
  for (const f of list) {
    firesMap.set(f.id, f);
    addFireGroup(f);
  }
  // Re-bind focus if the focused fire survived the full replace.
  const cur = getFireFocus();
  if (cur) {
    const f = firesMap.get(cur.id);
    refreshFireFocus(f ? fireHandle(f) : null);
  }
  syncFireAmbience();
  onFiresChanged();
}

/** One fire lit/burned out (broadcast). */
export function applyFireDelta(op: "add" | "remove", fire?: FireDeploy, id?: string): void {
  if (op === "add" && fire) {
    removeFireGroup(fire.id); // belt-and-braces against a double add
    firesMap.set(fire.id, fire);
    addFireGroup(fire);
    if (getFireFocus()?.id === fire.id) refreshFireFocus(fireHandle(fire));
    // Whoosh when the blaze appears (local success or a remote lighting nearby).
    playSfx(IGNITE_SFX, 0.5);
  } else if (op === "remove" && id) {
    firesMap.delete(id);
    removeFireGroup(id);
  }
  syncFireAmbience();
  onFiresChanged();
}

/** Map swap (plan 24): drop every mirrored fire — the new map's `fires` sync
 *  rebuilds the set (rides the held/replayed message queue). */
export function resetCampfires(): void {
  clearFireFocus();
  setFires([]);
}

/** The live fire mirror (game.ts's night-lamp refresh reads positions). */
export function getFires(): FireDeploy[] {
  return [...firesMap.values()];
}

export function getFireById(id: string): FireDeploy | undefined {
  return firesMap.get(id);
}

// ---- Focus picker + action-bar Extinguish ---------------------------------------

function pickFire(rc: THREE.Raycaster): { fire: FireHandle; distance: number } | null {
  let best: { fire: FireHandle; distance: number } | null = null;
  for (const [id, g] of fireGroups) {
    const f = firesMap.get(id);
    if (!f) continue;
    const hits = rc.intersectObject(g, true);
    if (!hits.length) continue;
    const d = hits[0]!.distance;
    if (!best || d < best.distance) best = { fire: fireHandle(f), distance: d };
  }
  return best;
}

/** Extinguish slot: own focused fire only (hidden while any input lock). */
export function extinguishSlotState(): {
  show: boolean; enabled: boolean; label: string; icon: string;
} {
  if (inputLocked()) return { show: false, enabled: false, label: "", icon: "" };
  const f = getFireFocus();
  if (!f || !f.mine) return { show: false, enabled: false, label: "", icon: "" };
  const p = game.player.position;
  const near = Math.hypot(p.x - f.x, p.z - f.z) <= CT.hearthRange + 0.5;
  return {
    show: true,
    enabled: near,
    label: near ? "Extinguish" : "Too far",
    icon: "💦",
  };
}

export function extinguishSlotAction(): void {
  const f = getFireFocus();
  if (!f || !f.mine || inputLocked()) return;
  const p = game.player.position;
  if (Math.hypot(p.x - f.x, p.z - f.z) > CT.hearthRange + 0.5) {
    flashToast("Too far to extinguish");
    return;
  }
  sendExtinguishFire(f.id);
}

// ---- Init + per-frame --------------------------------------------------------------

/** `onChanged` fires after every mirror mutation — game.ts hooks its night-lamp
 *  refresh here (a lit fire glows; lightSpots is otherwise a static snapshot). */
export function initCampfires(s: THREE.Scene, onChanged?: () => void): void {
  scene = s;
  if (onChanged) onFiresChanged = onChanged;
  setLiveFires(() => firesMap.values());
  registerFirePicker(pickFire);
  document.getElementById("extinguishBtn")?.addEventListener("click", extinguishSlotAction);
  // Warm ignite + ambient so the first light / walk-up isn't silent.
  preloadSfx(IGNITE_SFX, AMBIENT_TRACK);
}

/** Per-frame: flicker every burning fire's flames; refresh focused readyAt. */
export function updateCampfires(t: number): void {
  for (const g of fireGroups.values()) {
    updateFlames(g.userData.flames as THREE.Object3D, t);
  }
  const cur = getFireFocus();
  if (cur) {
    const f = firesMap.get(cur.id);
    if (!f) clearFireFocus();
    else if (f.readyAt !== cur.readyAt || !!f.mine !== cur.mine) {
      refreshFireFocus(fireHandle(f));
    }
  }
}
